/**
 * tb-thumbnail-vertical-nav.js
 *
 * Premium product hero (tb-product-hero) má vertical thumbnail column nalevo od main image.
 * Dawn slider-component řeší jen horizontální posun (scrollLeft). Pro vertikální column potřebujeme:
 *   1) up/down arrow buttons (zobrazí se jen když je kam posunout)
 *   2) click & drag scroll
 *
 * Skript injektuje šipky DO existující `.thumbnail-slider` (žádný liquid fork),
 * naváže scroll handlery, a auto-skrývá tlačítka podle scrollTop / scrollHeight.
 */

(function () {
  const SCROLL_STEP_PX = 200; // klik na šipku posune o ~3 thumby
  const DRAG_THRESHOLD_PX = 4; // pohyb < 4px = pořád klik (nezruš focus na thumbnail)
  const ANIM_DURATION_MS = 280; // smooth scroll animace — snappy ale ne instant

  function arrowSvg(direction) {
    const rotation = direction === 'up' ? '180' : '0';
    return (
      '<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" ' +
      'style="transform: rotate(' + rotation + 'deg)">' +
      '<path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>'
    );
  }

  function initSlider(slider) {
    if (slider.dataset.tbVerticalNavInit === '1') return;
    slider.dataset.tbVerticalNavInit = '1';

    const list = slider.querySelector('.thumbnail-list');
    if (!list) return;

    const upBtn = document.createElement('button');
    upBtn.type = 'button';
    upBtn.className = 'tb-thumb-nav tb-thumb-nav--up';
    upBtn.setAttribute('aria-label', 'Předchozí náhledy');
    upBtn.innerHTML = arrowSvg('up');

    const downBtn = document.createElement('button');
    downBtn.type = 'button';
    downBtn.className = 'tb-thumb-nav tb-thumb-nav--down';
    downBtn.setAttribute('aria-label', 'Další náhledy');
    downBtn.innerHTML = arrowSvg('down');

    slider.appendChild(upBtn);
    slider.appendChild(downBtn);

    function updateButtons() {
      const canScrollUp = list.scrollTop > 2;
      const canScrollDown = list.scrollTop + list.clientHeight < list.scrollHeight - 2;
      upBtn.classList.toggle('is-visible', canScrollUp);
      downBtn.classList.toggle('is-visible', canScrollDown);
    }

    // Custom RAF smooth scroll — native `behavior: smooth` má krátkou
    // browser-default duration (Chrome ~200-300ms) co dělá short distance
    // scroll skoro instant. 450ms ease-out je notably hladký.
    let scrollAnimRaf = null;
    function smoothScrollTo(targetTop, duration) {
      if (scrollAnimRaf) cancelAnimationFrame(scrollAnimRaf);
      const maxScroll = Math.max(0, list.scrollHeight - list.clientHeight);
      const clampedTarget = Math.max(0, Math.min(targetTop, maxScroll));
      const startTop = list.scrollTop;
      const distance = clampedTarget - startTop;
      if (Math.abs(distance) < 1) {
        scrollAnimRaf = null;
        return;
      }
      const startTime = performance.now();
      function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
      function step(now) {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        list.scrollTop = startTop + distance * easeOutCubic(progress);
        if (progress < 1) {
          scrollAnimRaf = requestAnimationFrame(step);
        } else {
          scrollAnimRaf = null;
        }
      }
      scrollAnimRaf = requestAnimationFrame(step);
    }

    upBtn.addEventListener('click', function (e) {
      e.preventDefault();
      smoothScrollTo(list.scrollTop - SCROLL_STEP_PX, ANIM_DURATION_MS);
    });
    downBtn.addEventListener('click', function (e) {
      e.preventDefault();
      smoothScrollTo(list.scrollTop + SCROLL_STEP_PX, ANIM_DURATION_MS);
    });

    list.addEventListener('scroll', updateButtons, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(updateButtons).observe(list);
    } else {
      window.addEventListener('resize', updateButtons);
    }
    // initial render po načtení thumbs
    requestAnimationFrame(updateButtons);
    setTimeout(updateButtons, 250);

    // ── Auto-scroll: když se zvolí thumb (klik / klávesnice / main-image swipe),
    // odsroluj list tak, aby aktivní thumb byl uprostřed viditelné oblasti
    // (Tom: "thumbnails se odscrolluje jakoby doprostřed").
    // Dva trigger pathy:
    //   1) MutationObserver na aria-current="true" — pokryje VŠECHNY zdroje
    //      selekce (Dawn media-gallery.js toggluje aria-current přes
    //      removeAttribute + setAttribute na thumb buttonech)
    //   2) Click listener na listu (bubble) — defensive fallback pokud
    //      observer z nějakého důvodu nezachytí. Drag cancellation v capture
    //      tento bubble handler nezablokuje pro suppressed clicks.
    function scrollThumbIntoView(li) {
      // Vždycky centruj active thumb (i když je už plně vidět) → uživatel
      // proklikávající 1,2,3,4,5... vidí list postupně scrollovat thumb po
      // thumbu. Bounds-clamp v smoothScrollTo.
      const liCenter = li.offsetTop + li.offsetHeight / 2;
      smoothScrollTo(liCenter - list.clientHeight / 2, ANIM_DURATION_MS);
    }

    // Dedup: Dawn's `slideChanged` event (debounced 500ms) volá setActiveThumbnail
    // znova i pro stejný thumb → aria-current se remove + set znova → observer
    // fire 500ms po prvním scrollu → animace běží 2x ("provede, dokončí a pak
    // po chvilce ještě hned" — Tom 2026-05-20). Trackneme last active btn a
    // skipneme pokud je to ten samý.
    let lastActiveBtn = null;
    const ariaObserver = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        if (m.attributeName !== 'aria-current') continue;
        const btn = m.target;
        if (!(btn instanceof Element)) continue;
        if (btn.getAttribute('aria-current') !== 'true') continue;
        if (btn === lastActiveBtn) continue;
        lastActiveBtn = btn;
        const li = btn.closest('li');
        if (li && list.contains(li)) scrollThumbIntoView(li);
      }
    });
    // Subtree: true catches changes na všechny descendant nodes uvnitř listu,
    // nezávisle na tom kdy se buttony rendrovaly. Click fallback bubble listener
    // jsme dropli — dělal duplicitní scroll call ~30ms po observeru a interrupoval
    // animaci (vypadalo to instant). Pokud observer jednou selže, řešíme tehdy.
    ariaObserver.observe(list, {
      attributes: true,
      attributeFilter: ['aria-current'],
      subtree: true
    });

    // ── Drag to scroll ──
    // KEY: `.is-dragging` class (která přes CSS dává `pointer-events: none`
    // na všechny children) se aplikuje AŽ po překročení threshold — quick click
    // na thumbnail teda nikdy is-dragging nevyvolá a click flow funguje normálně.
    let mouseDown = false;
    let dragMoved = false;
    let suppressNextClick = false;
    let startY = 0;
    let startScrollTop = 0;
    let suppressTimer = null;

    function cancelScrollAnim() {
      if (scrollAnimRaf) {
        cancelAnimationFrame(scrollAnimRaf);
        scrollAnimRaf = null;
      }
    }

    // Pointerdown pokrývá touch + mouse + pen — když user začne dragovat
    // list (na mobilu touchstart), zruš running smooth-scroll, ať native
    // scroll převezme. Bez tohohle RAF každý frame přepisoval scrollTop
    // a vypadalo to "vrací se zpět animace" (Tom 2026-05-20 mobile).
    list.addEventListener('pointerdown', cancelScrollAnim);
    // Wheel scroll taky cancel ať se naše anim nehádá s user-initiated.
    list.addEventListener('wheel', cancelScrollAnim, { passive: true });

    list.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      mouseDown = true;
      dragMoved = false;
      startY = e.pageY;
      startScrollTop = list.scrollTop;
    });

    document.addEventListener('mousemove', function (e) {
      if (!mouseDown) return;
      const dy = startY - e.pageY;
      if (!dragMoved && Math.abs(dy) > DRAG_THRESHOLD_PX) {
        dragMoved = true;
        list.classList.add('is-dragging');
      }
      if (dragMoved) {
        list.scrollTop = startScrollTop + dy;
        e.preventDefault(); // prevent text selection během dragu
      }
    });

    document.addEventListener('mouseup', function () {
      if (!mouseDown) return;
      mouseDown = false;
      list.classList.remove('is-dragging');
      if (dragMoved) {
        // Synthetic click po dragu zruší (mouseup target s pointer-events:none
        // končí na listu, click target = list/ancestor). Time-limit 80ms ať se
        // stale flag nezachytí do nesouvisejícího kliku.
        suppressNextClick = true;
        if (suppressTimer) clearTimeout(suppressTimer);
        suppressTimer = setTimeout(function () {
          suppressNextClick = false;
        }, 80);
      }
      dragMoved = false;
    });

    list.addEventListener(
      'click',
      function (e) {
        if (suppressNextClick) {
          e.stopPropagation();
          e.preventDefault();
          suppressNextClick = false;
          if (suppressTimer) {
            clearTimeout(suppressTimer);
            suppressTimer = null;
          }
        }
      },
      true
    );
  }

  function initAll(root) {
    const sliders = (root || document).querySelectorAll('.tb-product-hero__gallery .thumbnail-slider');
    sliders.forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
